import React, { useMemo } from 'react';
import usePlantafel, { getISOWeek } from '../hooks/usePlantafel';
import PlantafelLeftPanel from './PlantafelLeftPanel';
import { PlantafelBoardHeader } from './PlantafelBoardHeader';
import { PlantafelBoardGrid } from './PlantafelBoardGrid';
import { PlantafelBoardModals } from './PlantafelBoardModals';
import { usePlantafelHandlers } from './usePlantafelHandlers';
import { getHourMarkers } from '../utils/plantafelTime';
import styles from './PlantafelBoard.module.css';

const COL_WIDTH_WEEK = 160;
const COL_WIDTH_MONTH = 38;
const COL_WIDTH_YEAR = 24;
const LABEL_WIDTH = 120;

export default function PlantafelBoard(): React.JSX.Element {
  const hook = usePlantafel();

  const h = usePlantafelHandlers({
    loading: hook.loading,
    error: hook.error,
    laneGroups: hook.laneGroups,
    shifts: hook.shifts,
    backlogTasks: hook.backlogTasks,
    weekStart: hook.weekStart,
    days: hook.days,
    settings: hook.settings,
    selectedShift: hook.selectedShift,
    refresh: hook.refresh,
    handleShiftClick: hook.handleShiftClick,
    scheduleBacklogTask: hook.scheduleBacklogTask,
    updateShiftOptimistically: hook.updateShiftOptimistically,
    viewMode: hook.viewMode,
    setViewMode: hook.setViewMode,
    anchorDate: hook.anchorDate,
    setAnchorDate: hook.setAnchorDate,
  });

  const hourMarkers = useMemo(
    () => getHourMarkers(hook.settings.day_start_minutes, hook.settings.day_end_minutes, 1),
    [hook.settings.day_start_minutes, hook.settings.day_end_minutes]
  );

  const formatPeriodLabel = (): string => {
    switch (hook.viewMode) {
      case 'month': {
        const monthNames = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];
        return `${monthNames[hook.anchorDate.getMonth()]} ${hook.anchorDate.getFullYear()}`;
      }
      case 'year': return `${hook.anchorDate.getFullYear()}`;
      case 'week':
      default: {
        const endDate = new Date(hook.weekStart);
        endDate.setDate(endDate.getDate() + 6);
        const startStr = `${hook.weekStart.getDate().toString().padStart(2, '0')}.${(hook.weekStart.getMonth() + 1).toString().padStart(2, '0')}`;
        const endYear = endDate.getFullYear();
        const endStr = `${endDate.getDate().toString().padStart(2, '0')}.${(endDate.getMonth() + 1).toString().padStart(2, '0')}.${endYear}`;
        return `${startStr} - ${endStr}`;
      }
    }
  };

  const getPeriodPrefix = (): string => {
    switch (hook.viewMode) {
      case 'month': return '';
      case 'year': return 'Jahr ';
      case 'week':
      default: return `KW ${getISOWeek(hook.weekStart)} | `;
    }
  };

  const COL_WIDTH = hook.viewMode === 'year' ? COL_WIDTH_YEAR : hook.viewMode === 'month' ? COL_WIDTH_MONTH : COL_WIDTH_WEEK;
  const gridWidth = LABEL_WIDTH + hook.days.length * COL_WIDTH;

  return (
    <div className={styles.boardContainer} data-testid="plantafel-board">
      <PlantafelLeftPanel
        tasks={h.filteredBacklogTasks}
        meta={hook.backlogMeta}
        selectedShift={hook.selectedShift}
        onTaskClick={hook.handleTaskClick}
        onUnscheduleShift={h.handleUnscheduleShiftDrop}
        onRefresh={hook.refresh}
        assignedAssetMap={h.assignedAssetMap}
        assignedEmployeeMap={h.assignedEmployeeMap}
        employeeHoursMap={h.employeeHoursMap}
        onHighlightShift={h.handleHighlightShift}
        absences={h.absences}
        days={hook.days}
        onAbsentEmployeeClick={h.handleAbsentEmployeeClick}
        projectsTimeline={h.projectsTimeline}
      />

      <div className={styles.mainColumn}>
        <PlantafelBoardHeader
          loading={hook.loading}
          viewMode={hook.viewMode}
          setViewMode={hook.setViewMode}
          goToPreviousPeriod={hook.goToPreviousPeriod}
          goToNextPeriod={hook.goToNextPeriod}
          goToCurrentPeriod={hook.goToCurrentPeriod}
          refresh={hook.refresh}
          periodPrefix={getPeriodPrefix()}
          periodLabel={formatPeriodLabel()}
        />

        <div className={styles.contentArea}>
          <PlantafelBoardGrid
            error={hook.error}
            loading={hook.loading}
            hasTeams={hook.hasTeams}
            shifts={hook.shifts}
            laneGroups={hook.laneGroups}
            waSimple={h.waSimple}
            waComplex={h.waComplex}
            filteredShifts={h.filteredShifts}
            taskMap={h.taskMap}
            weekStart={hook.weekStart}
            weekStartStr={h.weekStartStr}
            days={hook.days}
            colWidth={COL_WIDTH}
            gridWidth={gridWidth}
            settings={hook.settings}
            hourMarkers={hourMarkers}
            viewMode={hook.viewMode}
            absences={h.absences}
            absenceCountsByDate={h.absenceCountsByDate}
            activeEmployeeId={h.activeEmployeeId}
            teamDropTargetId={h.teamDropTargetId}
            highlightedShiftId={h.highlightedShiftId}
            showProjectsTimeline={h.showProjectsTimeline}
            projectsTimeline={h.projectsTimeline}
            allProjects={h.allProjects}
            selectedProjectId={h.selectedProjectId}
            refresh={hook.refresh}
            handleShiftClick={hook.handleShiftClick}
            moveShiftToTime={hook.moveShiftToTime}
            resizeShift={hook.resizeShift}
            rescheduleShift={hook.rescheduleShift}
            handleDropTask={h.handleDropTask}
            handleAssignEmployee={h.handleAssignEmployee}
            handleAssignAsset={h.handleAssignAsset}
            handleUnscheduleShiftDrop={h.handleUnscheduleShiftDrop}
            handleCreateJobClick={h.handleCreateJobClick}
            handleEditRoster={h.handleEditRoster}
            handleAddTeamMember={h.handleAddTeamMember}
            handleRemoveTeamMember={h.handleRemoveTeamMember}
            handleAddTeamAsset={h.handleAddTeamAsset}
            handleRemoveTeamAsset={h.handleRemoveTeamAsset}
            setTeamDropTargetId={h.setTeamDropTargetId}
            setSelectedProjectId={h.setSelectedProjectId}
            handleCreateProject={h.handleCreateProject}
            handleDropProject={h.handleDropProject}
            handleOpenProject={h.handleOpenProject}
            setAbsModal={h.setAbsModal}
          />
        </div>
      </div>

      <PlantafelBoardModals
        sidePanelOpen={hook.sidePanelOpen}
        selectedShift={hook.selectedShift}
        selectedTask={hook.selectedTask}
        closeSidePanel={hook.closeSidePanel}
        refresh={hook.refresh}
        teamModalOpen={h.teamModalOpen}
        selectedWorkArea={h.selectedWorkArea}
        editingTeam={h.editingTeam}
        handleTeamModalClose={h.handleTeamModalClose}
        handleTeamSaved={h.handleTeamSaved}
        rosterModalOpen={h.rosterModalOpen}
        selectedTeamForRoster={h.selectedTeamForRoster}
        weekStartStr={h.weekStartStr}
        handleRosterModalClose={h.handleRosterModalClose}
        handleRosterSaved={h.handleRosterSaved}
        conflictState={hook.conflictState}
        closeConflictModal={hook.closeConflictModal}
        pushConflicts={hook.pushConflicts}
        employeeConflictModal={h.employeeConflictModal}
        setEmployeeConflictModal={h.setEmployeeConflictModal}
        handleReplanEmployee={h.handleReplanEmployee}
        createJobModal={h.createJobModal}
        allTeams={h.allTeams}
        handleCreateJobModalClose={h.handleCreateJobModalClose}
        handleJobCreated={h.handleJobCreated}
        scheduleModal={h.scheduleModal}
        laneGroups={hook.laneGroups}
        days={hook.days}
        closeScheduleModal={h.closeScheduleModal}
        scheduleBacklogTask={hook.scheduleBacklogTask}
        createProjectModalOpen={h.createProjectModalOpen}
        setCreateProjectModalOpen={h.setCreateProjectModalOpen}
        handleProjectCreated={h.handleProjectCreated}
        absModal={h.absModal}
        setAbsModal={h.setAbsModal}
        absences={h.absences}
      />
    </div>
  );
}
